import React from 'react';
import { Box, Typography } from '@mui/material';
import CanvasCardFrame, { CanvasCardDragSharedProps, mixSolidColor } from './CanvasCardFrame';
import { useLoadNoteQuery } from '../services/notes';
import { useAppSelector } from '../hooks';
import { selectAllEntries } from '../containers/contentSlice';
import { selectIsAuthenticated } from '../containers/authSlice';
import type { NoteVariant } from '../types';
import { canvasZoomProofWorldPx } from '../common/util';
import { NOTE_CARD_Z_INDEX } from '../utils/canvasZIndex';

const LONG_NOTE_MAX_WIDTH_PX = 510;
const LONG_NOTE_MIN_WIDTH_PX = 120;
const LONG_NOTE_MAX_WIDTH_NO_SHELL_PADDING_PX = 17;

export interface CanvasNoteCardProps extends CanvasCardDragSharedProps {
	noteId: string;
	label: string;
	variant?: NoteVariant;
	/** Note surface color (bookmark palette hex). */
	color?: string | null;
	isDraft?: boolean;
	/** The location bundle fills this note's query cache, so the card's own request waits for it. */
	isBundleLoading: boolean;
	onMenuClick: (e: React.MouseEvent) => void;
	setMenuAnchorEl: (el: HTMLElement | null) => void;
}

export function CanvasNoteCard({
	noteId,
	label,
	variant = 'default',
	color,
	isDraft,
	isBundleLoading,
	position,
	isSelected,
	scale,
	collectionColor,
	isPositionLocked,
	onPositionChange,
	constrainPosition,
	getDragTilt,
	onDragStart,
	onDragActivate,
	onDragMove,
	onDragEnd,
	cardNodeRef,
	onFocus,
	onBlur,
	onSelect,
}: CanvasNoteCardProps) {
	const isHeader = variant === 'header';
	const isLong = variant === 'long';
	const zoomProofFontPx = canvasZoomProofWorldPx(18, scale);
	const zoomProofRem = zoomProofFontPx / 16;
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allEntries = useAppSelector(selectAllEntries);
	const { data: loadedNote } = useLoadNoteQuery(noteId, {
		skip: !isAuthenticated || !noteId || Boolean(isDraft) || isBundleLoading,
	});
	const localSubRecordCount = React.useMemo(
		() => allEntries.filter((entry) => entry.parentId === noteId).length,
		[allEntries, noteId]
	);
	const subRecordCount = isAuthenticated && !isDraft
		? loadedNote?.subRecordCount ?? 0
		: localSubRecordCount;
	const shouldHideShell = isHeader && !isSelected;
	const noteBackgroundSx = color
		? {
			backgroundColor: (theme: { palette: { mode: string; background: { default: string } } }) =>
				mixSolidColor(theme.palette.background.default, color, isSelected ? 0.32 : theme.palette.mode === 'dark' ? 0.22 : 0.16),
		}
		: undefined;

	return (
		<CanvasCardFrame
			position={position}
			scale={scale}
			isSelected={isSelected}
			collectionColor={collectionColor}
			isPositionLocked={isPositionLocked}
			onPositionChange={onPositionChange}
			constrainPosition={constrainPosition}
			getDragTilt={getDragTilt}
			onDragStart={onDragStart}
			onDragActivate={onDragActivate}
			onDragMove={onDragMove}
			onDragEnd={onDragEnd}
			cardNodeRef={cardNodeRef}
			onFocus={onFocus}
			onBlur={onBlur}
			onSelect={onSelect}
			ariaLabel={label || 'Untitled'}
			zIndex={NOTE_CARD_Z_INDEX}
			contentShell={shouldHideShell ? 'none' : 'default'}
			sx={
				isHeader
					? {
						boxShadow: isSelected ? undefined : null,
						backgroundColor: 'none',
						borderWidth: canvasZoomProofWorldPx(1, scale),
						maxWidth: canvasZoomProofWorldPx(250, scale),
						minWidth: 0,
						p: zoomProofRem,
						px: zoomProofRem,
						borderRadius: zoomProofRem * 2,
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
					}
					: isLong
						? {
							maxWidth: LONG_NOTE_MAX_WIDTH_PX,
							minWidth: LONG_NOTE_MIN_WIDTH_PX,
							px: shouldHideShell ? `${LONG_NOTE_MAX_WIDTH_NO_SHELL_PADDING_PX}px` : undefined,
							py: shouldHideShell ? 1 : undefined,
							borderRadius: 2,
							...noteBackgroundSx,
						}
						: noteBackgroundSx
			}
		>
			<Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={0.5} sx={{ minWidth: 0 }}>
				<Typography
					variant={isHeader ? 'subtitle2' : 'body2'}
					sx={{
						flex: 1,
						minWidth: 0,
						wordBreak: 'break-word',
						whiteSpace: 'pre-wrap',
						py: isHeader ? 0 : 0.25,
						...(isHeader
							? {
								fontWeight: 400,
								// lineHeight: 1.35,
								fontSize: `${zoomProofFontPx}px`,
								letterSpacing: 0.01,
								// ...classes.maxHeight2Lines,
								overflow: 'hidden',
								display: '-webkit-box',
								'-webkit-line-clamp': '2',
								'-webkit-box-orient': 'vertical',
							}
							: {}),
					}}
					title={label}
				>
					{label || 'Untitled'}
				</Typography>
			</Box>
			{subRecordCount > 0 && scale >= 1 && <Typography
				textAlign="right"
				color="action.disabled"
				variant="caption"
				sx={{ fontSize: '0.625rem', lineHeight: 1.5, position: 'absolute', right: 4, bottom: 0 }}
			>
				(+{subRecordCount})
			</Typography>}
		</CanvasCardFrame>
	);
}

/** Memoized so panning/zooming (which re-renders CanvasView every frame) doesn't also re-render every note card — CanvasView keeps this card's props referentially stable across those frames specifically so this bail-out holds. */
export default React.memo(CanvasNoteCard);
